import { useCallback, useEffect, useState } from 'react';
import { cn } from '../../lib/cn';
import { playSound } from '../../audio/sound';
import { Button } from '../../components/Button';

export interface PinPadProps {
  /** Мінімум цифр, щоб кнопка входу стала активною. */
  minLength?: number;
  /** Стеля вводу. PIN тут лише дитячий: 4–6 цифр. */
  maxLength?: number;
  onSubmit: (pin: string) => void;
  disabled?: boolean;
  /** Показати трус і почистити поле — коли PIN не підійшов. */
  errorNonce?: number;
  label?: string;
}

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', 'clear', '0', 'back'] as const;

/**
 * PIN-пад із великими кнопками (мінімум 64px) — під дитячий палець на телефоні.
 * Керується і з клавіатури: цифри, Backspace, Enter, Escape.
 *
 * Автовідправки на четвертій цифрі свідомо НЕМАЄ: PIN буває і довшим за
 * чотири цифри, автосабміт обривав би його на півдорозі. Натомість є явна
 * кнопка — заразом вона прибирає випадкові входи від «протицяв екран».
 */
export function PinPad({
  minLength = 4,
  maxLength = 6,
  onSubmit,
  disabled,
  errorNonce = 0,
  label = 'Введи свій PIN',
}: PinPadProps): React.JSX.Element {
  const [pin, setPin] = useState('');
  const [shake, setShake] = useState(0);

  const ready = pin.length >= minLength && !disabled;

  useEffect(() => {
    if (errorNonce > 0) {
      setPin('');
      setShake((n) => n + 1);
    }
  }, [errorNonce]);

  const submit = useCallback((): void => {
    if (pin.length < minLength || disabled) return;
    onSubmit(pin);
  }, [pin, minLength, disabled, onSubmit]);

  useEffect(() => {
    const handler = (event: KeyboardEvent): void => {
      if (disabled) return;
      if (event.key >= '0' && event.key <= '9') {
        playSound('tap');
        setPin((c) => (c.length >= maxLength ? c : c + event.key));
      } else if (event.key === 'Backspace') setPin((c) => c.slice(0, -1));
      else if (event.key === 'Escape') setPin('');
      else if (event.key === 'Enter') submit();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [disabled, maxLength, submit]);

  const push = (digit: string): void => {
    if (disabled) return;
    playSound('tap');
    setPin((current) => (current.length >= maxLength ? current : current + digit));
  };

  // Крапок показуємо стільки, скільки введено, але не менше мінімуму —
  // інакше поле «стрибає» на кожній цифрі.
  const dots = Math.max(minLength, pin.length);

  return (
    <div className="flex flex-col items-center gap-5">
      <p className="text-sm font-bold text-white/70">{label}</p>

      <div
        key={shake}
        className={cn('flex gap-3', shake > 0 && 'lk-anim-shake')}
        role="status"
        aria-label={`Введено ${pin.length} цифр`}
      >
        {Array.from({ length: dots }, (_, index) => (
          <span
            key={index}
            className={cn(
              'h-5 w-5 rounded-full border-2 transition-all',
              index < pin.length
                ? 'scale-110 border-[var(--lk-gold)] bg-[var(--lk-gold)]'
                : 'border-white/30 bg-white/5',
            )}
          />
        ))}
      </div>

      <div className="grid w-full max-w-[19rem] grid-cols-3 gap-3">
        {KEYS.map((key) => {
          if (key === 'clear') {
            return (
              <button
                key={key}
                type="button"
                className="lk-btn lk-btn--ghost h-16 text-xs font-black"
                onClick={() => {
                  playSound('tap');
                  setPin('');
                }}
                disabled={disabled}
              >
                Очистити
              </button>
            );
          }
          if (key === 'back') {
            return (
              <button
                key={key}
                type="button"
                className="lk-btn lk-btn--ghost h-16 text-xs font-black"
                onClick={() => {
                  playSound('tap');
                  setPin((c) => c.slice(0, -1));
                }}
                disabled={disabled}
              >
                Стерти
              </button>
            );
          }
          return (
            <button
              key={key}
              type="button"
              className="lk-btn h-16 text-2xl"
              onClick={() => push(key)}
              disabled={disabled}
              aria-label={`Цифра ${key}`}
            >
              {key}
            </button>
          );
        })}
      </div>

      <Button
        tone="gold"
        size="xl"
        block
        mute
        disabled={!ready}
        loading={disabled}
        onClick={submit}
        className="max-w-[19rem]"
      >
        Увійти
      </Button>
    </div>
  );
}
